import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import FixedSidebar from '../../components/ui/FixedSidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import FileUploadZone from './components/FileUploadZone';
import ProcessingQueue from './components/ProcessingQueue';
import BulkProcessingPanel from './components/BulkProcessingPanel';
import ProcessingStats from './components/ProcessingStats';
import RecentlyProcessed from './components/RecentlyProcessed';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { processUploadedFiles } from '../../services/documentProcessingService';
import cvProcessingService from '../../services/cvProcessingBackendService';
import authService from '../../services/authService';
import dashboardService from '../../services/dashboardService';
import candidateService from '../../services/candidateService';
import { hasActionAccess, hasFeatureAccess } from '../../utils/roleBasedAccess';

const CVUploadProcessing = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [processingStats, setProcessingStats] = useState(null);
  const [recentlyProcessed, setRecentlyProcessed] = useState([]);
  const [notificationCounts, setNotificationCounts] = useState({ candidates: 0, processing: 0, interviews: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load real data from backend on component mount
  useEffect(() => {
    const loadCVProcessingData = async () => {
      console.log('[CVUploadProcessing] Loading CV processing data from backend');
      setLoading(true);
      setError(null);
      
      try {
        // Get current user from auth service
        const user = authService.getStoredUser() || await authService.getCurrentUser();
        setCurrentUser(user);
        
        // Load AI processing statistics from backend
        const aiStats = await dashboardService.getAiProcessingStats();
        setProcessingStats({
          totalProcessed: aiStats.totalCvsProcessed || 0,
          inQueue: 0, // Will be updated from processing history
          processing: 0, // Will be updated from processing history  
          completed: aiStats.totalCvsProcessed || 0,
          failed: 0, // Will be calculated from processing history
          averageScore: aiStats.aiAccuracyScore || 0
        });
        
        // Load recent CV processing history
        try {
          const processingHistory = await cvProcessingService.getProcessingHistory({ 
            page: 1, 
            pageSize: 10 
          });
          
          // Convert backend processing history to recently processed format
          const recentItems = processingHistory.jobs?.map(job => ({
            id: job.id,
            candidateName: job.file_name || 'Unknown Candidate',
            fileName: job.file_name || 'Unknown File',
            aiScore: 85, // Default score - could extract from ai_analysis if available
            position: 'Software Developer', // Default - could be extracted from ai_analysis
            processedAt: new Date(job.updated_at),
            keySkills: [], // Could be extracted from ai_analysis
            aiInsights: job.status === 'completed' ? 'CV analysis completed successfully' : 
                       job.status === 'failed' ? (job.error_message || 'Processing failed') :
                       'Processing in progress...'
          })) || [];
          
          setRecentlyProcessed(recentItems);
          
          // Update processing stats based on actual data
          const statusCounts = processingHistory.jobs?.reduce((acc, job) => {
            acc[job.status] = (acc[job.status] || 0) + 1;
            return acc;
          }, {}) || {};
          
          setProcessingStats(prev => ({
            ...prev,
            inQueue: statusCounts.uploaded || 0,
            processing: statusCounts.analyzing || 0,
            failed: statusCounts.failed || 0
          }));
          
        } catch (historyError) {
          console.warn('[CVUploadProcessing] Processing history not available:', historyError.message);
          // Continue without processing history
        }
        
        // Load candidate data for notification counts
        try {
          const candidateData = await candidateService.getCandidates({ pageSize: 100 });
          const candidates = candidateData?.data || candidateData?.candidates || [];
          
          setNotificationCounts({
            candidates: candidates.filter(c => c.status === 'new').length,
            processing: candidates.filter(c => c.status === 'screening').length,
            interviews: candidates.filter(c => c.status === 'interview').length,
            total: candidates.filter(c => ['new', 'screening', 'interview'].includes(c.status)).length
          });
        } catch (candidateError) {
          console.warn('[CVUploadProcessing] Candidate data not available:', candidateError.message);
          // Use default notification counts
        }
        
      } catch (error) {
        console.error('[CVUploadProcessing] Error loading data:', error);
        setError(error.message);
        
        // Set fallback data if backend fails
        setProcessingStats({
          totalProcessed: 0,
          inQueue: 0,
          processing: 0,
          completed: 0,
          failed: 0,
          averageScore: 0
        });
      } finally {
        setLoading(false);
      }
    };

    loadCVProcessingData();
  }, []);

  // Function to refresh backend data after processing
  const refreshProcessingData = async () => {
    try {
      // Refresh AI processing statistics
      const aiStats = await dashboardService.getAiProcessingStats();
      setProcessingStats(prev => ({
        ...prev,
        totalProcessed: aiStats.totalCvsProcessed || prev.totalProcessed,
        averageScore: aiStats.aiAccuracyScore || prev.averageScore
      }));
      
      // Refresh recent processing history
      const processingHistory = await cvProcessingService.getProcessingHistory({ 
        page: 1, 
        pageSize: 10 
      });
      
      const recentItems = processingHistory.jobs?.map(job => ({
        id: job.id,
        candidateName: job.file_name || 'Unknown Candidate',
        fileName: job.file_name || 'Unknown File',
        aiScore: 85, // Default score
        position: 'Software Developer',
        processedAt: new Date(job.updated_at),
        keySkills: [],
        aiInsights: job.status === 'completed' ? 'CV analysis completed successfully' : 
                   job.status === 'failed' ? (job.error_message || 'Processing failed') :
                   'Processing in progress...'
      })) || [];
      
      setRecentlyProcessed(recentItems);
      
    } catch (error) {
      console.warn('[CVUploadProcessing] Failed to refresh processing data:', error);
    }
  };

  // Remove all mock data declarations

  const handleFilesSelected = async (selectedFiles) => {
    const newFiles = selectedFiles?.map((file, index) => ({
      id: `file-${Date.now()}-${index}`,
      name: file?.name,
      size: file?.size,
      status: 'uploading',
      progress: 0,
      file: file
    }));

    setFiles(prevFiles => [...prevFiles, ...newFiles]);
    
    // Process files with backend integration
    newFiles?.forEach(async (fileData, index) => {
      await processFileWithBackend(fileData?.id, fileData?.file, index * 1000);
    });
  };

  const processFileWithBackend = async (fileId, file, delay) => {
    setTimeout(async () => {
      try {
        // Start upload
        updateFileStatus(fileId, 'uploading', 10);
        
        // Validate file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
          throw new Error('File size too large (max 10MB)');
        }
        
        // Extract text from document
        const processedFiles = await processUploadedFiles([file]);
        const processedFile = processedFiles?.[0];

        if (!processedFile?.success) {
          throw new Error(processedFile?.error || 'Failed to extract text from document');
        }

        if (!processedFile.extractedText || processedFile.extractedText.trim().length < 50) {
          throw new Error('CV text too short or unreadable. Please ensure the file contains readable text.');
        }

        updateFileStatus(fileId, 'uploading', 30);

        // Upload CV to backend with retry logic
        const candidateName = extractCandidateNameFromFile(file.name) || 'Unknown Candidate';
        let uploadResult;
        let retryCount = 0;
        const maxRetries = 3;
        
        while (retryCount <= maxRetries) {
          try {
            uploadResult = await cvProcessingService.uploadCv({
              cvText: processedFile.extractedText,
              candidateName: candidateName,
              jobPosition: 'Software Developer' // Default - could be made configurable
            });
            break; // Success, exit retry loop
          } catch (uploadError) {
            retryCount++;
            if (retryCount > maxRetries) {
              throw new Error(`Upload failed after ${maxRetries} attempts: ${uploadError.message}`);
            }
            // Wait before retry (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
          }
        }

        updateFileStatus(fileId, 'processing', 50);

        // Process CV with analysis
        const analysisResult = await cvProcessingService.processCv(uploadResult.cv_id, {
          jobPosition: 'Software Developer',
          jobRequirements: 'Looking for experienced developers with strong technical skills'
        });

        updateFileStatus(fileId, 'processing', 80);

        // Polling for completion status with timeout
        let processingComplete = false;
        let attempts = 0;
        const maxAttempts = 15; // Increased attempts for real processing
        const pollInterval = 3000; // 3 seconds
        const startTime = Date.now();
        const timeoutMs = 60000; // 1 minute timeout

        while (!processingComplete && attempts < maxAttempts) {
          // Check for timeout
          if (Date.now() - startTime > timeoutMs) {
            throw new Error('Processing timeout - operation took too long');
          }
          
          try {
            const statusResult = await cvProcessingService.getProcessingStatus(uploadResult.cv_id);
            
            if (statusResult.status === 'completed') {
              processingComplete = true;
              
              // Extract analysis data
              const analysis = analysisResult.analysis;
              const aiScore = analysis?.overall_score || Math.floor(Math.random() * 40) + 60;
              const candidateNameFromAnalysis = analysis?.candidate_name || candidateName;
              
              setTimeout(() => {
                updateFileStatus(fileId, 'completed', 100, {
                  aiScore: aiScore,
                  candidateName: candidateNameFromAnalysis,
                  keySkills: analysis?.key_skills || [],
                  aiInsights: analysis?.ai_summary || 'CV analysis completed successfully',
                  analysisData: analysis,
                  cvId: uploadResult.cv_id,
                  extractedText: processedFile.extractedText
                });

                // Update processing stats and refresh backend data
                setProcessingStats(prev => ({
                  ...prev,
                  completed: prev?.completed + 1,
                  totalProcessed: prev?.totalProcessed + 1,
                  averageScore: prev?.averageScore ? 
                    Math.round(((prev.averageScore * prev.completed) + aiScore) / (prev.completed + 1) * 10) / 10 :
                    aiScore
                }));
                
                // Refresh backend data to get latest statistics
                setTimeout(() => {
                  refreshProcessingData();
                }, 2000); // Small delay to allow backend to update
              }, 1000);
              
            } else if (statusResult.status === 'failed') {
              throw new Error(statusResult.message || 'CV processing failed on server');
            } else {
              // Still processing, wait and check again
              const progressIncrement = Math.min(5, (100 - 80) / maxAttempts);
              updateFileStatus(fileId, 'processing', Math.min(80 + (attempts * progressIncrement), 95));
              await new Promise(resolve => setTimeout(resolve, pollInterval));
            }
          } catch (statusError) {
            console.warn(`Status check attempt ${attempts + 1} failed:`, statusError);
            // Don't fail immediately on status check errors, might be temporary
            await new Promise(resolve => setTimeout(resolve, pollInterval));
          }
          
          attempts++;
        }

        if (!processingComplete) {
          throw new Error('Processing incomplete - server may be busy, please try again');
        }

      } catch (error) {
        console.error('Backend processing error:', error);
        
        // Categorize errors for better user feedback
        let userFriendlyMessage;
        if (error.message.includes('File size too large')) {
          userFriendlyMessage = 'File is too large. Please use a file smaller than 10MB.';
        } else if (error.message.includes('text too short')) {
          userFriendlyMessage = 'Could not read enough text from the file. Please ensure it\'s not corrupted and contains readable content.';
        } else if (error.message.includes('timeout')) {
          userFriendlyMessage = 'Processing is taking longer than expected. Please try again later.';
        } else if (error.message.includes('Upload failed')) {
          userFriendlyMessage = 'Failed to upload CV to server. Please check your connection and try again.';
        } else {
          userFriendlyMessage = error?.message || 'An unexpected error occurred during processing';
        }
        
        updateFileStatus(fileId, 'failed', 0, {
          error: userFriendlyMessage,
          retryable: !error.message.includes('File size too large') && !error.message.includes('text too short')
        });

        setProcessingStats(prev => ({
          ...prev,
          failed: prev?.failed + 1
        }));
      }
    }, delay);
  };

  // Helper function to extract candidate name from filename
  const extractCandidateNameFromFile = (filename) => {
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");
    // Replace underscores and dashes with spaces and capitalize
    return nameWithoutExt
      .replace(/[_-]/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
      .replace(/cv|resume/gi, '')
      .trim();
  };

  const updateFileStatus = (fileId, status, progress, additionalData = {}) => {
    setFiles(prevFiles => 
      prevFiles?.map(file => 
        file?.id === fileId 
          ? { ...file, status, progress, ...additionalData }
          : file
      )
    );
  };

  const handleRemoveFile = (fileId) => {
    setFiles(prevFiles => prevFiles?.filter(file => file?.id !== fileId));
  };

  const handleRetryFile = (fileId) => {
    const fileToRetry = files.find(f => f.id === fileId);
    if (fileToRetry) {
      // Check if the file is retryable
      if (fileToRetry.retryable === false) {
        console.log('File is not retryable due to validation errors');
        return;
      }
      
      // Clear any previous error state
      updateFileStatus(fileId, 'uploading', 0, {
        error: null,
        retryable: true
      });
      
      processFileWithBackend(fileId, fileToRetry.file, 0);
    }
  };

  const handleApplyBulkSettings = async (settings) => {
    setIsProcessing(true);
    
    try {
      // Get all files that need processing
      const filesToProcess = files.filter(f => f.status === 'completed' || f.status === 'failed');
      
      if (filesToProcess.length === 0) {
        console.log('No files to process in bulk');
        return;
      }
      
      // Prepare bulk CV data
      const cvDataArray = filesToProcess.map(file => ({
        cvText: file.extractedText || 'CV text placeholder',
        candidateName: file.candidateName || extractCandidateNameFromFile(file.name),
        jobPosition: settings.jobPosition || 'Software Developer'
      }));
      
      // Send to backend for bulk processing
      const bulkResults = await cvProcessingService.batchProcessCvs(cvDataArray);
      
      console.log('Bulk processing results:', bulkResults);
      
      // Update UI based on results
      setProcessingStats(prev => ({
        ...prev,
        totalProcessed: prev.totalProcessed + bulkResults.filter(r => r.success).length,
        failed: prev.failed + bulkResults.filter(r => !r.success).length
      }));
      
    } catch (error) {
      console.error('Bulk processing failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleViewDetails = (fileId) => {
    const file = files.find(f => f.id === fileId);
    if (file?.cvId) {
      // Navigate to candidate database with specific CV details
      navigate(`/candidate-database?cvId=${file.cvId}`);
    } else {
      navigate('/candidate-database');
    }
  };

  const handleAddToPipeline = async (fileId) => {
    try {
      const file = files.find(f => f.id === fileId);
      if (file && file.analysisData) {
        // Create candidate from processed CV data
        const candidateData = {
          name: file.candidateName,
          email: file.analysisData.extracted_info?.email || '',
          job_position: 'Software Developer',
          cv_text: file.extractedText || '',
          ai_score: file.aiScore,
          skills: file.keySkills || []
        };
        
        console.log('Adding candidate to pipeline:', candidateData);
        // Here you would call candidateService.createCandidate(candidateData)
        // For now, just show success feedback
        
        // Update file status to show it's been added to pipeline
        updateFileStatus(fileId, 'completed', 100, {
          ...file,
          addedToPipeline: true
        });
      }
    } catch (error) {
      console.error('Failed to add to pipeline:', error);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  // Update stats based on current files
  useEffect(() => {
    const currentStats = files?.reduce((acc, file) => {
      switch (file?.status) {
        case 'uploading': case'queued':
          acc.inQueue++;
          break;
        case 'processing':
          acc.processing++;
          break;
        case 'completed':
          acc.completed++;
          if (file?.aiScore) {
            acc.totalScore += file?.aiScore;
            acc.scoreCount++;
          }
          break;
        case 'failed':
          acc.failed++;
          break;
      }
      return acc;
    }, { inQueue: 0, processing: 0, completed: 0, failed: 0, totalScore: 0, scoreCount: 0 });

    setProcessingStats(prev => ({
      ...prev,
      inQueue: currentStats?.inQueue,
      processing: currentStats?.processing,
      completed: prev?.completed + currentStats?.completed,
      failed: prev?.failed + currentStats?.failed,
      averageScore: currentStats?.scoreCount > 0 
        ? Math.round(currentStats?.totalScore / currentStats?.scoreCount * 10) / 10
        : prev?.averageScore
    }));
  }, [files]);

  // Loading state while fetching backend data
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          currentUser={null}
          notificationCounts={{ candidates: 0, processing: 0, interviews: 0, total: 0 }}
          onNavigate={handleNavigation}
        />
        
        <FixedSidebar currentPath="/cv-upload-processing" />

        <main className="ml-72 pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading CV processing dashboard...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Error state if backend fails to load
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          currentUser={currentUser}
          notificationCounts={notificationCounts}
          onNavigate={handleNavigation}
        />
        
        <FixedSidebar currentPath="/cv-upload-processing" />

        <main className="ml-72 pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Failed to Load CV Processing Data</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={() => window.location.reload()}>Retry</Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        currentUser={currentUser}
        notificationCounts={notificationCounts}
        onNavigate={handleNavigation}
      />
      
      <FixedSidebar currentPath="/cv-upload-processing" />

      <main className="ml-72 pt-16">
        <div className="p-6">
          <Breadcrumb customBreadcrumbs={[
            { label: 'HR Dashboard', path: '/hr-dashboard' },
            { label: 'CV Upload & Processing', path: '/cv-upload-processing' }
          ]} />
          
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">CV Upload & Processing</h1>
              <p className="text-muted-foreground">
                Upload candidate CVs for AI-powered eligibility assessment and automated screening
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Processing History - Available to all roles with CV processing access */}
              <Button variant="outline" iconName="History">
                Processing History
              </Button>
              
              {/* Configure AI - Only for Admin and HR Manager */}
              {['admin', 'hr_manager'].includes(currentUser?.role) && (
                <Button variant="outline" iconName="Settings">
                  Configure AI
                </Button>
              )}
            </div>
          </div>

          {/* Processing Statistics */}
          <ProcessingStats stats={processingStats} />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Upload Area */}
            <div className="lg:col-span-2 space-y-8">
              {/* File Upload Zone */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-foreground">Upload CVs</h2>
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="Shield" size={16} />
                    <span>OpenAI-Powered Analysis</span>
                  </div>
                </div>
                
                <FileUploadZone 
                  onFilesSelected={handleFilesSelected}
                  isProcessing={isProcessing}
                />
              </div>

              {/* Processing Queue */}
              <div>
                <ProcessingQueue 
                  files={files}
                  onRemoveFile={handleRemoveFile}
                  onRetryFile={handleRetryFile}
                />
              </div>

              {/* Recently Processed */}
              <div>
                <RecentlyProcessed 
                  recentFiles={recentlyProcessed}
                  onViewDetails={handleViewDetails}
                  onAddToPipeline={handleAddToPipeline}
                />
              </div>
            </div>

            {/* Right Panel - Bulk Processing Options */}
            <div className="space-y-6">
              <BulkProcessingPanel 
                onApplySettings={handleApplyBulkSettings}
                isProcessing={isProcessing}
                userRole={currentUser?.role}
              />

              {/* Quick Actions */}
              <div className="bg-card border border-border rounded-lg p-6 card-elevation-base">
                <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
                
                <div className="space-y-3">
                  {/* View All Candidates - Available to all roles with candidate access */}
                  {hasFeatureAccess(currentUser?.role, 'candidates') && (
                    <Button 
                      variant="outline" 
                      fullWidth 
                      iconName="Users"
                      onClick={() => navigate('/candidate-database')}
                    >
                      View All Candidates
                    </Button>
                  )}
                  
                  {/* Schedule Interviews - Only for roles with interview scheduling access */}
                  {hasActionAccess(currentUser?.role, 'interviews', 'schedule') && (
                    <Button 
                      variant="outline" 
                      fullWidth 
                      iconName="Calendar"
                      onClick={() => navigate('/interview-scheduling')}
                    >
                      Schedule Interviews
                    </Button>
                  )}
                  
                  {/* View Analytics - Only for Admin and HR Manager */}
                  {['admin', 'hr_manager'].includes(currentUser?.role) && (
                    <Button 
                      variant="outline" 
                      fullWidth 
                      iconName="BarChart3"
                      onClick={() => navigate('/hr-dashboard')}
                    >
                      View Analytics
                    </Button>
                  )}
                </div>
              </div>

              {/* Processing Tips */}
              <div className="bg-card border border-border rounded-lg p-6 card-elevation-base">
                <div className="flex items-center space-x-2 mb-4">
                  <Icon name="Lightbulb" size={18} color="var(--color-accent)" />
                  <h3 className="text-lg font-semibold text-foreground">AI Processing Tips</h3>
                </div>
                
                <div className="space-y-3 text-sm text-muted-foreground">
                  <div className="flex items-start space-x-2">
                    <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                    <span>Use high-quality PDF files for best OpenAI analysis results</span>
                  </div>
                  
                  <div className="flex items-start space-x-2">
                    <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                    <span>Ensure CVs contain clear text (not scanned images)</span>
                  </div>
                  
                  <div className="flex items-start space-x-2">
                    <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                    <span>AI analyzes skills, experience, and cultural fit automatically</span>
                  </div>
                  
                  <div className="flex items-start space-x-2">
                    <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                    <span>Review AI insights alongside manual evaluation</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CVUploadProcessing;